/* oxlint-disable no-inner-declarations */
import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';

import type { AlertSuggestion } from '@/task-manager/entities/alert-suggestion';
import { TaskCardType } from '@/task-manager/entities/task-card-type';
import { TaskPriority } from '@/task-manager/entities/task-priority';
import { TaskStatus } from '@/task-manager/entities/task-status';
import { Optimistic } from '@/utils/react-query/optimistic';

import { AlertStatus } from '../../entities/alert-status';
import {
  type BodyOf,
  type CreateCardViewOutputSerizalizer,
  tasksV1CardsNewCreate,
} from '../fetch-client';
import { ListTasks } from '../queries/list-tasks';
import { useFetchOpts } from '../use-fetch-opts';

const taskPriorityFromAlertStatus: Record<AlertStatus, TaskPriority> = {
  [AlertStatus.CRITICAL]: TaskPriority.HIGH,
  [AlertStatus.TRIPPED]: TaskPriority.MEDIUM,
  [AlertStatus.WARNING]: TaskPriority.MEDIUM,
  [AlertStatus.INFO]: TaskPriority.LOW,
  [AlertStatus.OK]: TaskPriority.LOW,
};

export namespace CreateAlertTask {
  export type Input = {
    status: TaskStatus;
    alertSuggestion: AlertSuggestion;
  };
  export type Output = {
    id: string;
  };

  function bodyFromInput({
    status,
    alertSuggestion,
  }: Input): BodyOf<typeof tasksV1CardsNewCreate> {
    return {
      createCardViewInputRequest: {
        card_type: TaskCardType.ALERT,
        title: alertSuggestion.frontendId,
        status,
        priority: taskPriorityFromAlertStatus[alertSuggestion.status],
        // TODO: remove `accure_id` has it as been replaced by `finding_id` (validate with Matthias before, to be sure it's 100% ok)
        accure_id: alertSuggestion.accureId,
        finding_id: alertSuggestion.id,
        level: alertSuggestion.level,
      },
    };
  }

  function outputFromResult(result: CreateCardViewOutputSerizalizer): Output {
    return {
      id: result.uuid,
    };
  }

  export function useMutation() {
    const getOpts = useFetchOpts();
    const queryClient = rq.useQueryClient();
    const [listTasksFilters] = ListTasks.useGetFiltersFromQueryParams();

    return rq.useMutation<Output, unknown, Input>({
      async mutationFn(input) {
        const opts = await getOpts();
        const body = bodyFromInput(input);
        const result = await ok(tasksV1CardsNewCreate(body, opts));

        const output = outputFromResult(result);

        return output;
      },
      onMutate(input) {
        queryClient.cancelQueries(ListTasks.key({ filters: listTasksFilters }));
        queryClient.setQueryData(
          ListTasks.key({ filters: listTasksFilters }),
          ListTasks.optimistic((previous) => ({
            ...previous,
            // Delete the alert suggestion
            alertSuggestions: Optimistic.deleteItem(
              previous.alertSuggestions,
              ({ id }) => id === input.alertSuggestion.id,
            ),
            // Create the temporary task
            tasks: [
              ...previous.tasks,
              {
                id: Optimistic.id(),
                cardType: TaskCardType.ALERT,
                priority:
                  taskPriorityFromAlertStatus[input.alertSuggestion.status],
                status: input.status,
                title: input.alertSuggestion.frontendId,
                alert: {
                  status: input.alertSuggestion.status,
                  suggestedAction: input.alertSuggestion.suggestedAction,
                  resolved: false,
                },
              },
            ],
          })),
        );
      },
      async onSettled() {
        await queryClient.invalidateQueries({
          queryKey: ListTasks.key({ filters: listTasksFilters }),
        });
      },
      async onSuccess() {
        await queryClient.invalidateQueries({
          queryKey: ['alerts', 'list-alerts'],
        });
      },
    });
  }
}
